/**
 * @file Controlled Drugs history table filter owner.
 */

import {
    appData,
    saveViewSettings,
} from "../../app/state/state.js";
import {
    bindTableFilterControls,
    doesTableRowMatchFilter,
    getTableFilterMenuHtml,
    isTableFilterActive,
    positionOpenTableFilterMenu,
} from "../../shared/ui/tableFilterControls.js";
import {
    getUserFullDisplayName,
} from "../../shared/utils/userDisplay.js";
import {
    sortDrugsByOrder,
    sortLocationsByOrder,
    sortUsersByDrugOrder,
} from "./drugUtils.js";

const TRANSACTION_TYPE_FILTER_OPTIONS = Object.freeze([
    "use",
    "restock",
    "relocate",
    "audit",
    "correction",
    "loss",
    "void",
]);

const TRANSACTION_STATUS_FILTER_OPTIONS = Object.freeze([
    "pending",
    "pending_approval",
    "pending_finalization",
    "approved",
    "automatic_approval",
    "rejected",
    "escalated",
    "void",
]);

const USER_FILTER_GROUP_DRUG_ROLES = "Users with drug roles";
const USER_FILTER_GROUP_OTHERS = "Others";
const FILTER_GROUP_OTHER_VALUES = "Other values";

const drugHistoryFilterState = {
    draftFilters: {},
    filters: {},
    menuPosition: {},
    openFilterKey: "",
    sortDirection: "desc",
    sortKey: "transactionDate",
};

function getDatePart(value) {
    if (!value) {return "";}
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10);
    }

    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) {return "";}

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
}

function formatTransactionTypeLabel(type) {
    if (!type) {return "Unknown";}
    if (type === "pending_approval") {return "Pending Approval";}
    if (type === "pending_finalization") {return "Pending Finalisation";}
    return String(type)
        .replace(/^drug_/, "")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatTransactionStatusLabel(status) {
    if (!status) {return "Unknown";}
    if (status === "pending_approval") {return "Pending Approval";}
    if (status === "pending_finalization") {return "Pending Finalisation";}
    if (status === "automatic_approval") {return "Automatic Approval";}
    return String(status)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getPayloadDrugOptions(transaction) {
    const inventory = appData?.drugInventory || [];
    const values = [];
    const labels = [];

    (transaction.payload || []).forEach((item) => {
        if (!item?.drugId) {return;}
        const drug = inventory.find((entry) => entry.id === item.drugId);
        values.push(String(item.drugId));
        labels.push(drug ? (drug.shortName || drug.name || item.drugId) : item.drugId);
    });

    return { labels, values };
}

function getPayloadLocationOptions(transaction) {
    const locations = appData?.config?.drugs?.locations || [];
    const values = [];
    const labels = [];

    (transaction.payload || []).forEach((item) => {
        if (!item?.location) {return;}
        const location = locations.find((entry) => entry.id === item.location);
        values.push(String(item.location));
        labels.push(location ? (location.name || item.location) : item.location);
    });

    return { labels, values };
}

function getPayloadQuantityValues(transaction) {
    return (transaction.payload || [])
        .map((item) => Math.abs(Number(item?.quantity)))
        .filter((value) => Number.isFinite(value));
}

function getPayloadBalanceValues(transaction) {
    return (transaction.payload || [])
        .map((item) => Number(item?.balanceAfter))
        .filter((value) => Number.isFinite(value));
}

function getPerformedByLabel(userId) {
    const user = (appData?.users || []).find((entry) => entry.id === userId);
    return user ? getUserFullDisplayName(user) : "Unknown User";
}

function getFilterOption(value, label, group = "") {
    const optionValue = String(value ?? "");
    const optionLabel = String(label ?? optionValue);
    return {
        value: optionValue,
        label: optionLabel,
        searchLabel: optionLabel.toLowerCase(),
        ...(group ? { group } : {}),
    };
}

function addFilterOption(optionsByValue, option) {
    if (!option?.value || optionsByValue.has(option.value)) {return;}
    optionsByValue.set(option.value, option);
}

function mergeFilterOptions(...optionSets) {
    const optionsByValue = new Map();
    optionSets.flat().forEach((option) => addFilterOption(optionsByValue, option));
    return Array.from(optionsByValue.values());
}

function getRowDerivedFilterOptions(rows = [], columnKey, valueKey, group = "") {
    const optionsByValue = new Map();
    rows.forEach((row) => {
        const rawValue = valueKey && Object.prototype.hasOwnProperty.call(row, valueKey) ?
            row[valueKey] :
            row[columnKey];
        const values = Array.isArray(rawValue) ? rawValue : [rawValue];
        const displayValue = row[columnKey];
        const displayValues = Array.isArray(displayValue) ? displayValue : [];
        values.forEach((entry, index) => {
            const value = String(entry ?? "");
            if (!value || optionsByValue.has(value)) {return;}
            const label = String(displayValues[index] ?? displayValue ?? value);
            optionsByValue.set(value, getFilterOption(value, label || value, group));
        });
    });
    return Array.from(optionsByValue.values());
}

function getDrugRoleMemberIds() {
    const memberIds = new Set();
    (appData?.drugRoles || []).forEach((role) => {
        (role.memberIds || []).forEach((userId) => memberIds.add(userId));
    });
    return memberIds;
}

function userHasDrugRole(user, roleMemberIds) {
    return Boolean(
        user?.isDrugAdmin ||
        user?.drugRole ||
        (Array.isArray(user?.drugRoles) && user.drugRoles.length > 0) ||
        roleMemberIds.has(user?.id),
    );
}

function getAllUserFilterOptions(rows = [], valueKey = "performedByValue", columnKey = "performedBy") {
    const roleMemberIds = getDrugRoleMemberIds();
    const users = sortUsersByDrugOrder(appData?.users || []);
    const usersWithDrugRoles = users.filter((user) => userHasDrugRole(user, roleMemberIds));
    const otherUsers = users.filter((user) => !userHasDrugRole(user, roleMemberIds));

    return mergeFilterOptions(
        usersWithDrugRoles.map((user) => {
            return getFilterOption(user.id, getUserFullDisplayName(user), USER_FILTER_GROUP_DRUG_ROLES);
        }),
        otherUsers.map((user) => {
            return getFilterOption(user.id, getUserFullDisplayName(user), USER_FILTER_GROUP_OTHERS);
        }),
        getRowDerivedFilterOptions(rows, columnKey, valueKey, FILTER_GROUP_OTHER_VALUES),
    );
}

function getDrugFilterOptions(rows = []) {
    const configuredDrugs = sortDrugsByOrder(appData?.drugInventory || [])
        .map((drug) => getFilterOption(drug.id, drug.shortName || drug.name || drug.id));
    return mergeFilterOptions(
        configuredDrugs,
        getRowDerivedFilterOptions(rows, "drug", "drugValues", FILTER_GROUP_OTHER_VALUES),
    );
}

function getLocationFilterOptions(rows = []) {
    const configuredLocations = sortLocationsByOrder(appData?.config?.drugs?.locations || [])
        .map((location) => getFilterOption(location.id, location.name || location.id));
    return mergeFilterOptions(
        configuredLocations,
        getRowDerivedFilterOptions(rows, "location", "locationValues", FILTER_GROUP_OTHER_VALUES),
    );
}

function getTransactionTypeFilterOptions(rows = []) {
    return mergeFilterOptions(
        TRANSACTION_TYPE_FILTER_OPTIONS.map((type) => getFilterOption(type, formatTransactionTypeLabel(type))),
        getRowDerivedFilterOptions(rows, "type", "typeValue", FILTER_GROUP_OTHER_VALUES),
    );
}

function getTransactionStatusFilterOptions(rows = []) {
    return mergeFilterOptions(
        TRANSACTION_STATUS_FILTER_OPTIONS.map((status) => {
            return getFilterOption(status, formatTransactionStatusLabel(status));
        }),
        getRowDerivedFilterOptions(rows, "status", "statusValue", FILTER_GROUP_OTHER_VALUES),
    );
}

function getDrugHistoryFilterOptions(columnKey, rows = []) {
    if (columnKey === "type") {return getTransactionTypeFilterOptions(rows);}
    if (columnKey === "drug") {return getDrugFilterOptions(rows);}
    if (columnKey === "location") {return getLocationFilterOptions(rows);}
    if (columnKey === "performedBy" || columnKey === "approvedBy") {
        return getAllUserFilterOptions(
            rows,
            columnKey === "approvedBy" ? "approvedByValue" : "performedByValue",
            columnKey,
        );
    }
    if (columnKey === "status") {return getTransactionStatusFilterOptions(rows);}
    return [];
}

function getDrugHistoryFilterColumns(columns = [], rows = []) {
    return columns.map((column) => {
        if (column?.filter?.kind !== "checkboxList") {return column;}
        const options = getDrugHistoryFilterOptions(column.key, rows);
        if (options.length === 0) {return column;}
        return {
            ...column,
            filter: {
                ...column.filter,
                options,
            },
        };
    });
}

function getLookupName(items, id, fallback = "") {
    const match = (items || []).find((entry) => entry.id === id);
    return match ? (match.shortName || match.name || id || fallback) : (id || fallback);
}

function getDrugHistoryNotesText(transaction) {
    const parts = [];

    if (transaction.type === "audit" && transaction.auditId) {
        const audit = appData.drugAudits?.find((entry) => entry.id === transaction.auditId);
        parts.push("Audit", audit?.auditNumber ? `Audit ${audit.auditNumber}` : "");
        parts.push(getLookupName(appData.users, audit?.startedBy, ""));
        parts.push(getLookupName(appData.users, audit?.finalizedBy, ""));
    }

    if (transaction.type === "void" && transaction.voidedTransactionId) {
        parts.push("Voiding", transaction.voidedTransactionId);
        parts.push(transaction.voidReason, transaction.justification);
    }

    if (transaction.provider) {
        parts.push("Provider", getLookupName(appData.users, transaction.provider, transaction.provider));
    }

    parts.push(
        transaction.patientName,
        transaction.batchCode,
        transaction.orderNumber,
        transaction.justification,
        transaction.voidReason,
        transaction.approverNote,
    );

    if (transaction.supplier) {
        const supplier = getLookupName(
            appData.config?.drugs?.suppliers,
            transaction.supplier,
            transaction.supplier,
        );
        parts.push("Supplier", supplier);
    }

    if (transaction.type === "correction" && transaction.auditId) {
        parts.push("From audit", transaction.auditId);
    }

    (transaction.payload || []).forEach((item) => {
        if (item?.justification) {
            parts.push(
                getLookupName(appData.drugInventory, item.drugId, "Unknown"),
                item.justification,
            );
        }
    });

    return parts
        .filter((part) => part !== null && part !== undefined && String(part).trim())
        .join(" ");
}

function syncDrugHistoryFilterState(settings) {
    if (!settings.filters) {settings.filters = {};}
    drugHistoryFilterState.filters = settings.filters;
    drugHistoryFilterState.sortKey = settings.sortBy || "transactionDate";
    drugHistoryFilterState.sortDirection = settings.sortDirection || "desc";
    return drugHistoryFilterState;
}

/**
 * Builds filter rows for Controlled Drugs history table helpers.
 * @param {Array<object>} transactions Source history transactions.
 * @returns {Array<object>} Table-filter row adapters.
 */
export function getDrugHistoryColumnFilterRows(transactions = []) {
    return transactions.map((transaction) => {
        const drugOptions = getPayloadDrugOptions(transaction);
        const locationOptions = getPayloadLocationOptions(transaction);
        return {
            transactionDate: getDatePart(transaction.transactionDate),
            transactionDateValue: getDatePart(transaction.transactionDate),
            type: formatTransactionTypeLabel(transaction.type),
            typeValue: String(transaction.type || ""),
            drug: drugOptions.labels,
            drugValues: drugOptions.values,
            location: locationOptions.labels,
            locationValues: locationOptions.values,
            quantityValues: getPayloadQuantityValues(transaction),
            balanceValues: getPayloadBalanceValues(transaction),
            notesText: getDrugHistoryNotesText(transaction),
            performedBy: getPerformedByLabel(transaction.performedBy),
            performedByValue: String(transaction.performedBy || ""),
            status: formatTransactionStatusLabel(transaction.status),
            statusValue: String(transaction.status || ""),
        };
    });
}

/**
 * Returns active Drugs history filter keys using approved table semantics.
 * @param {object} settings Drugs history view settings.
 * @param {Array<object>} columns Visible column definitions.
 * @returns {Array<string>} Active filter keys.
 */
export function getActiveDrugHistoryFilterKeys(settings, columns) {
    const filters = settings?.filters || {};
    return columns
        .filter((column) => isTableFilterActive(column, filters[column.key]))
        .map((column) => column.key);
}

/**
 * Applies approved table filters to Controlled Drugs history transactions.
 * @param {Array<object>} transactions Source history transactions.
 * @param {Array<object>} columns Visible column definitions.
 * @param {object} settings Drugs history view settings.
 * @returns {Array<object>} Filtered transactions.
 */
export function filterDrugHistoryTransactions(transactions, columns, settings) {
    const filters = settings?.filters || {};
    const rows = getDrugHistoryColumnFilterRows(transactions);
    const activeColumns = columns.filter((column) => {
        return isTableFilterActive(column, filters[column.key]);
    });

    if (activeColumns.length === 0) {return transactions;}

    return transactions.filter((transaction, index) => {
        const row = rows[index];
        return activeColumns.every((column) => {
            return doesTableRowMatchFilter(row, column, filters[column.key]);
        });
    });
}

/**
 * Returns render state for the Drugs history table header controls.
 * @param {object} settings Drugs history view settings.
 * @returns {object} Header control state.
 */
export function getDrugHistoryHeaderControlState(settings) {
    const tableState = syncDrugHistoryFilterState(settings);
    return {
        filters: tableState.filters,
        openFilterKey: tableState.openFilterKey,
        sortDirection: tableState.sortDirection,
        sortKey: tableState.sortKey,
        withControls: true,
    };
}

/**
 * Renders the open approved table filter menu for Drugs history.
 * @param {object} options Render options.
 * @param {object} options.settings Drugs history view settings.
 * @param {Array<object>} options.columns Visible column definitions.
 * @param {Array<object>} options.rows Source filter rows.
 * @returns {string} Filter menu HTML.
 */
export function renderDrugHistoryFilterMenu({ settings, columns, rows }) {
    const tableState = syncDrugHistoryFilterState(settings);
    const filterColumns = getDrugHistoryFilterColumns(columns, rows);
    return getTableFilterMenuHtml(tableState.openFilterKey, filterColumns, rows, tableState);
}

/**
 * Binds approved table filter controls to the Drugs history table wrapper.
 * @param {object} options Bind options.
 * @param {HTMLElement} options.wrapper Table wrapper element.
 * @param {object} options.settings Drugs history view settings.
 * @param {Array<object>} options.rows Source filter rows.
 * @param {Array<object>} options.columns Visible column definitions.
 * @param {Function} options.render Re-render callback.
 * @returns {void}
 */
export function bindDrugHistoryTableFilterControls({
    wrapper,
    settings,
    rows,
    columns,
    render,
}) {
    if (!wrapper || !settings) {return;}

    const tableState = syncDrugHistoryFilterState(settings);
    const filterColumns = getDrugHistoryFilterColumns(columns, rows);
    bindTableFilterControls(
        wrapper,
        tableState,
        () => {
            settings.filters = tableState.filters;
            settings.sortBy = tableState.sortKey;
            settings.sortDirection = tableState.sortDirection;
            saveViewSettings();
            render();
        },
        rows,
        () => filterColumns,
    );

    requestAnimationFrame(() => {
        positionOpenTableFilterMenu(wrapper, tableState);
    });
}
